import React, { useState } from 'react';
import { useI18n } from '@/context/I18nContext';
import { Dialog } from '@/components/common/Dialog';
import { rpc } from '@/api/client';
import { useToast } from '@/context/ToastContext';

interface BugReportDialogProps {
  open: boolean;
  onClose: () => void;
}

export const BugReportDialog: React.FC<BugReportDialogProps> = ({ open, onClose }) => {
  const { t, locale } = useI18n();
  const { showToast } = useToast();
  const [description, setDescription] = useState('');

  const handleSubmit = () => {
    if (!description.trim()) return;

    const desc = description;
    // Fire-and-forget: do not block the UI on the bug report network call
    void rpc('CreateBugReport', {
      description: desc,
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      appLanguage: locale
    }).catch((error) => {
      console.error('Failed to submit bug report:', error);
    });

    showToast(t('footer.submitted'), 'success');
    setDescription('');
    onClose();
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <button
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-text-muted hover:text-text-primary transition-colors cursor-pointer"
      >
        {t('common.cancel')}
      </button>
      <button
        onClick={handleSubmit}
        disabled={!description.trim()}
        className="px-4 py-2 rounded-md text-sm font-medium bg-accent text-white hover:bg-accent-hover disabled:opacity-50 transition-colors cursor-pointer"
      >
        {t('footer.submit')}
      </button>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('footer.bugReportTitle')}
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            {t('footer.description')}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('footer.descriptionPlaceholder')}
            className="w-full h-32 px-3 py-2 bg-bg-surface border border-border-subtle rounded-md text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
          />
        </div>
      </div>
    </Dialog>
  );
};
